import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Clock, ShieldCheck, Trash2, Loader2, Repeat, Lock, Route } from "lucide-react";
import { STATUS_STYLES, STATUS_LABEL, CATEGORIES } from "../lib/constants";
import { ReportButton } from "./ReportButton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

export function ErrandCard({ errand, index = 0, wide = false, currentUserId, onDelete }) {
  const cats = (errand.categories && errand.categories.length ? errand.categories : [errand.category]).filter(Boolean);
  const catLabels = cats.map((v) => CATEGORIES.find((c) => c.value === v)?.label || v);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const canDelete = onDelete && currentUserId && errand.requester_id === currentUserId && errand.status === "open";

  const confirmDelete = async () => {
    setBusy(true);
    await onDelete(errand.id);
    setBusy(false);
    setOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.05, 0.4) }}
      className={`relative ${wide ? "sm:col-span-2" : ""}`}
    >
      <Link
        to={`/errand/${errand.id}`}
        data-testid={`errand-card-${errand.id}`}
        className="group block h-full bg-white rounded-3xl p-6 border border-gray-100 shadow-soft hover:shadow-lift transition-all duration-300 hover:-translate-y-1"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex flex-wrap gap-1.5">
            {catLabels.map((label) => (
              <span key={label} className="text-xs font-bold uppercase tracking-wide text-secondary bg-secondary/10 px-3 py-1 rounded-full">
                {label}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            {errand.is_multi_stop && (
              <span data-testid={`errand-stops-${errand.id}`} className="text-xs font-bold px-2.5 py-1 rounded-full bg-primary/10 text-primary flex items-center gap-1">
                <Route size={11} /> {errand.stop_count} stops
              </span>
            )}
            {errand.recurrence && errand.recurrence !== "none" && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#EFEAFB] text-[#6D4FC0] flex items-center gap-1">
                <Repeat size={11} /> {errand.recurrence}
              </span>
            )}
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${STATUS_STYLES[errand.status]}`}>
              {STATUS_LABEL[errand.status]}
            </span>
          </div>
        </div>
        <h3 className="font-heading font-extrabold text-lg text-[#1A1D20] leading-snug mb-2 group-hover:text-primary transition-colors">
          {errand.title}
        </h3>
        <p className="text-sm text-[#6B7280] line-clamp-2 mb-4">{errand.description}</p>
        <div className="flex items-center gap-4 text-xs text-[#6B7280] mb-4">
          <span className="flex items-center gap-1">
            {errand.location_hidden ? <Lock size={12} className="text-[#6D4FC0]" /> : <MapPin size={13} />}
            {errand.location}{errand.location_hidden ? " · approx." : ""}
          </span>
          <span className="flex items-center gap-1"><Clock size={13} /> {errand.deadline}</span>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-accent/20 text-accent flex items-center justify-center text-xs font-bold">
              {errand.requester_name?.[0]?.toUpperCase()}
            </span>
            <span className="text-xs font-semibold text-[#4A5361]">{errand.requester_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-heading font-extrabold text-xl text-primary">${Number(errand.budget).toFixed(0)}</span>
            {currentUserId && errand.requester_id !== currentUserId && (
              <ReportButton targetType="errand" targetId={errand.id} />
            )}
            {canDelete && (
              <button
                type="button"
                data-testid={`delete-errand-${errand.id}`}
                aria-label="Delete errand"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#B23B3B] bg-[#FBE9E9]/60 hover:bg-[#FBE9E9] transition-colors"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        </div>
      </Link>

      {canDelete && (
        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogContent className="rounded-3xl" data-testid={`delete-dialog-${errand.id}`}>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">Delete this errand?</AlertDialogTitle>
              <AlertDialogDescription>
                "{errand.title}" will be permanently removed from Explore. This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`delete-no-${errand.id}`} className="rounded-full font-semibold">Keep it</AlertDialogCancel>
              <AlertDialogAction data-testid={`delete-yes-${errand.id}`} onClick={(ev) => { ev.preventDefault(); confirmDelete(); }} disabled={busy}
                className="rounded-full font-bold bg-[#B23B3B] hover:bg-[#9c3333]">
                {busy ? <Loader2 className="animate-spin" size={16} /> : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </motion.div>
  );
}

export function VerifiedBadges({ user, size = "sm" }) {
  const items = [];
  if (user.id_verified) items.push("ID Verified");
  if (user.phone_verified) items.push("Phone Verified");
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((t) => (
        <span key={t} data-testid={`badge-${t.replace(" ", "-").toLowerCase()}`}
          className="inline-flex items-center gap-1 bg-[#E7F6F0] text-[#2F8F6E] font-bold rounded-full px-3 py-1 text-xs">
          <ShieldCheck size={13} /> {t}
        </span>
      ))}
    </div>
  );
}
